import type { ContactValues, EmergencyContact, Patient, UpdatePatientData } from '../types/patient';

// Shown where a phone or email was saved before it was stored encrypted: it is
// on file, but only a one-way hash of it exists.
export const UNREADABLE_CONTACT_TEXT = 'On file, re-enter to view';

/** Text for a phone or email, or null when there is nothing on file. */
export function contactDisplay(value: string | undefined, unreadable: boolean | undefined): string | null {
  if (value) return value;
  return unreadable ? UNREADABLE_CONTACT_TEXT : null;
}

/** Edit form values for the patient's own phone and email. */
export function contactFormValues(patient: Patient): { phoneHash: string; emailHash: string } {
  return { phoneHash: patient.contact?.phone ?? '', emailHash: patient.contact?.email ?? '' };
}

/** Emergency contacts as the edit form holds them: the inputs show the readable values. */
export function emergencyContactsForForm(contacts: EmergencyContact[] | undefined): EmergencyContact[] {
  return (contacts ?? []).map((contact) => ({
    ...contact,
    phoneHash: contact.phone ?? '',
    emailHash: contact.email ?? '',
  }));
}

function fieldForSave(value: string | undefined, original: ContactValues | undefined, field: 'phone' | 'email') {
  if (value) return value;
  // Emptied: clear a value that was readable. An unreadable one was never shown,
  // so a blank input means "leave it", and nothing on file needs no change.
  return original?.[field] ? '' : undefined;
}

/** Contact block for an update; undefined when nothing changes. */
export function contactForSave(
  form: { phoneHash?: string; emailHash?: string } | undefined,
  original: Patient['contact']
): UpdatePatientData['contact'] {
  const phoneHash = fieldForSave(form?.phoneHash, original, 'phone');
  const emailHash = fieldForSave(form?.emailHash, original, 'email');
  if (phoneHash === undefined && emailHash === undefined) return undefined;
  return {
    ...(phoneHash !== undefined && { phoneHash }),
    ...(emailHash !== undefined && { emailHash }),
  };
}

/**
 * Emergency contacts for an update. A blank phone/email on a contact whose
 * stored value is unreadable sends its legacy hash back, so the server keeps
 * it; read-only fields are not sent.
 */
export function emergencyContactsForSave(
  contacts: EmergencyContact[] | undefined,
  original: EmergencyContact[] | undefined
): EmergencyContact[] {
  return (contacts ?? []).map((contact) => {
    const before = original?.find((c) => c.id === contact.id);
    return {
      id: contact.id,
      firstName: contact.firstName,
      lastName: contact.lastName,
      relationship: contact.relationship,
      phoneHash: contact.phoneHash || (before?.phoneUnreadable && before.phoneHash) || '',
      emailHash: contact.emailHash || (before?.emailUnreadable && before.emailHash) || '',
      isPrimary: contact.isPrimary,
      notes: contact.notes,
    };
  });
}
